import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import './DatePicker.css';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function parseValue(value) {
  if (!value || typeof value !== 'string') return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]) - 1,
    day: Number(match[3])
  };
}

function toValueString(year, month, day) {
  return `${String(year).padStart(4, '0')}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function DatePicker({
  id,
  name,
  value = '',
  onChange,
  required = false,
  disabled = false,
  className = '',
  placeholder = 'DD/MM/YYYY'
}) {
  const parsed = parseValue(value);
  const today = new Date();
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(parsed?.year ?? today.getFullYear());
  const [viewMonth, setViewMonth] = useState(parsed?.month ?? today.getMonth());
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const next = parseValue(value);
    if (next) {
      setViewYear(next.year);
      setViewMonth(next.month);
    }

    const onDocClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, value]);

  const days = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = [];

    for (let i = 0; i < firstDay; i += 1) {
      cells.push(null);
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push(day);
    }
    while (cells.length % 7 !== 0) {
      cells.push(null);
    }
    return cells;
  }, [viewYear, viewMonth]);

  const emitChange = (nextValue) => {
    if (!onChange) return;
    onChange({
      target: {
        name: name || id || '',
        value: nextValue,
        type: 'date'
      }
    });
  };

  const selectDay = (day) => {
    const nextValue = toValueString(viewYear, viewMonth, day);
    emitChange(nextValue);
    setOpen(false);
  };

  const goPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const isSelected = (day) => (
    !!parsed &&
    parsed.year === viewYear &&
    parsed.month === viewMonth &&
    parsed.day === day
  );

  const isToday = (day) => (
    today.getFullYear() === viewYear &&
    today.getMonth() === viewMonth &&
    today.getDate() === day
  );

  return (
    <div className={`app-date-picker ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        id={id}
        className={`app-date-picker-trigger${open ? ' is-open' : ''}`}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={value ? 'app-date-picker-value' : 'app-date-picker-placeholder'}>
          {value ? formatDateDDMMYYYY(value) : placeholder}
        </span>
        <span className="app-date-picker-icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
            <path d="M3 10H21" stroke="currentColor" strokeWidth="1.8" />
            <path d="M8 3V7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M16 3V7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </span>
      </button>

      {/* Keep native-compatible value for forms/required */}
      <input
        type="text"
        name={name}
        value={value || ''}
        required={required}
        readOnly
        tabIndex={-1}
        className="app-date-picker-hidden"
        aria-hidden="true"
      />

      {open && (
        <div className="app-date-picker-popup" role="dialog" aria-label="Choose date">
          <div className="app-date-picker-popup-head">
            <button type="button" className="app-date-picker-nav" onClick={goPrevMonth} aria-label="Previous month">
              ‹
            </button>
            <div className="app-date-picker-month-label">
              {MONTHS[viewMonth]} {viewYear}
            </div>
            <button type="button" className="app-date-picker-nav" onClick={goNextMonth} aria-label="Next month">
              ›
            </button>
          </div>

          <div className="app-date-picker-weekdays">
            {WEEKDAYS.map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>

          <div className="app-date-picker-grid">
            {days.map((day, index) => (
              day == null ? (
                <span key={`empty-${index}`} className="app-date-picker-day is-empty" />
              ) : (
                <button
                  key={`${viewYear}-${viewMonth}-${day}`}
                  type="button"
                  className={[
                    'app-date-picker-day',
                    isSelected(day) ? 'is-selected' : '',
                    isToday(day) ? 'is-today' : ''
                  ].filter(Boolean).join(' ')}
                  onClick={() => selectDay(day)}
                >
                  {day}
                </button>
              )
            ))}
          </div>

          <div className="app-date-picker-popup-foot">
            <button
              type="button"
              className="app-date-picker-today-btn"
              onClick={() => {
                const y = today.getFullYear();
                const m = today.getMonth();
                const d = today.getDate();
                setViewYear(y);
                setViewMonth(m);
                emitChange(toValueString(y, m, d));
                setOpen(false);
              }}
            >
              Today
            </button>
            <button
              type="button"
              className="app-date-picker-close-btn"
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DatePicker;
